import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/SubscriptionsService';
import {useState, useEffect} from 'react';
import {Subscription} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSubscriptions = (customerId?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const filter = customerId ? {customerId: customerId} : undefined;
    const queryKey = customerId ? ['subscriptions', {customerId: customerId}, pagination.currentToken] : ['subscriptions', pagination.currentToken];

    const subscriptionsQuery = useQuery({
        queryKey,
        queryFn: () => fetchRecords(filter, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (subscriptionsQuery.data) pagination.setNextToken(subscriptionsQuery.data.nextToken);
    }, [subscriptionsQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [customerId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Subscription deleted');
            queryClient.invalidateQueries({queryKey: ['subscriptions']});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['subscriptions']});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: Subscription[] = subscriptionsQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.customerId?.toLowerCase().includes(search) ||
                item.description?.toLowerCase().includes(search) ||
                item.status?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: subscriptionsQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
